import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('systemklart_token'));
  const [loading, setLoading] = useState(true);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      if (token) {
        try {
          // Verify token and get user data
          const [userResponse, orgResponse] = await Promise.all([
            axios.get(`${API}/users/me`),
            axios.get(`${API}/organizations/me`)
          ]);
          
          setUser(userResponse.data);
          setOrganization(orgResponse.data);
        } catch (error) {
          console.error('Auth initialization failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, user: userData, organization: orgData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      setOrganization(orgData);
      localStorage.setItem('systemklart_token', newToken);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Innlogging feilet'
      };
    }
  };

  const register = async (orgData, userData) => {
    try {
      // Combine org and user data for backend
      const payload = {
        orgnr: orgData.orgnr,
        name: orgData.name,
        industry: orgData.industry,
        employees: orgData.employees,
        address: orgData.address,
        website: orgData.website,
        description: orgData.description,
        email: userData.email,
        admin_name: userData.name,
        password: userData.password,
        phone: userData.phone,
        title: userData.title
      };
      
      const response = await axios.post(`${API}/auth/register`, payload);
      
      const { token: newToken, user: newUser, organization: newOrg } = response.data;
      
      setToken(newToken);
      setUser(newUser);
      setOrganization(newOrg);
      localStorage.setItem('systemklart_token', newToken);
      
      return { success: true };
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registrering feilet'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setOrganization(null);
    localStorage.removeItem('systemklart_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateOrganization = (updatedOrg) => {
    setOrganization(updatedOrg);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const isAdmin = () => hasRole(['Admin']);
  const isPowerUser = () => hasRole(['Admin', 'PowerUser']);

  const value = {
    user,
    organization,
    token,
    loading,
    login,
    register,
    logout,
    updateOrganization,
    updateUser,
    setOrganization,
    setUser,
    hasRole,
    isAdmin,
    isPowerUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};